import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import ConfirmDialog from "@/components/ConfirmDialog";
import CacheLogosButton from "@/components/admin/CacheLogosButton";
import { Plus, Edit2, Trash2, X, Tag } from "lucide-react";

const blank = (kind = "oem") => ({
    id: null,
    name: "",
    kind,
    logo_url: "",
    website: "",
    description: "",
    order: 0,
});

export default function BrandsTab() {
    const [brands, setBrands] = useState([]);
    const [filterKind, setFilterKind] = useState("all"); // all | oem | aftermarket
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/brands");
            setBrands(data);
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load brands");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setError("");
        const payload = {
            name: (editing.name || "").trim(),
            kind: editing.kind,
            logo_url: editing.logo_url || null,
            website: editing.website || null,
            description: editing.description || null,
            order: parseInt(editing.order, 10) || 0,
        };
        if (!payload.name) { setError("Name required"); return; }
        try {
            if (editing.id) {
                await api.put(`/admin/brands/${editing.id}`, payload);
            } else {
                await api.post("/admin/brands", payload);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (b) => {
        setConfirm({
            title: `Delete brand "${b.name}"?`,
            message: "Products linked to this brand will keep their data but lose the brand reference.",
            destructive: true,
            confirmText: "Delete",
            onConfirm: async () => {
                await api.delete(`/admin/brands/${b.id}`);
                setConfirm(null);
                await load();
            },
        });
    };

    const visible = brands.filter((b) => filterKind === "all" || b.kind === filterKind);

    const kindBadge = (kind) => (
        <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ${
                kind === "oem" ? "bg-slate-900 text-white" : "bg-amber-100 text-amber-900 border border-amber-300"
            }`}
        >
            {kind}
        </span>
    );

    return (
        <div data-testid="admin-brands-tab">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <h2 className="font-display font-bold uppercase text-xl flex items-center gap-2">
                        <Tag className="w-5 h-5" /> Brands
                    </h2>
                    <span className="text-xs font-mono text-slate-500">{brands.length} total</span>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={filterKind}
                        onChange={(e) => setFilterKind(e.target.value)}
                        className="input-field !w-auto"
                        data-testid="brand-kind-filter"
                    >
                        <option value="all">All kinds</option>
                        <option value="oem">OEM only</option>
                        <option value="aftermarket">Aftermarket only</option>
                    </select>
                    <button
                        onClick={() => setEditing(blank("oem"))}
                        className="btn-primary"
                        data-testid="new-oem-brand-button"
                    >
                        <Plus className="w-4 h-4" /> New OEM
                    </button>
                    <button
                        onClick={() => setEditing(blank("aftermarket"))}
                        className="btn-outline"
                        data-testid="new-aftermarket-brand-button"
                    >
                        <Plus className="w-4 h-4" /> New Aftermarket
                    </button>
                    <CacheLogosButton onDone={load} />
                </div>
            </div>

            {error && <div className="text-red-600 text-xs font-mono mb-3">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {visible.map((b) => (
                    <div
                        key={b.id}
                        className="border border-slate-200 bg-white p-3 flex flex-col gap-2"
                        data-testid={`brand-card-${b.id}`}
                    >
                        <div className="h-20 bg-slate-50 flex items-center justify-center border border-slate-100">
                            {b.logo_url ? (
                                <img
                                    src={b.logo_url}
                                    alt={b.name}
                                    className="max-h-16 max-w-[90%] object-contain"
                                />
                            ) : (
                                <span className="text-xs font-mono text-slate-400">no logo</span>
                            )}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <div className="font-display font-bold truncate">{b.name}</div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    {kindBadge(b.kind)}
                                    <span className="text-[10px] font-mono text-slate-500">
                                        {b.product_count || 0} prod
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button
                                    onClick={() => setEditing({ ...b })}
                                    className="p-1.5 hover:bg-slate-100"
                                    title="Edit"
                                    data-testid={`edit-brand-${b.id}`}
                                >
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => remove(b)}
                                    className="p-1.5 hover:bg-red-50 text-red-600"
                                    title="Delete"
                                    data-testid={`delete-brand-${b.id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {visible.length === 0 && (
                    <div className="col-span-full text-center py-10 text-sm font-mono text-slate-500 border border-dashed border-slate-200">
                        No brands yet. Create your first one.
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="brand-edit-modal">
                    <div className="bg-white max-w-lg w-full">
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="font-display font-bold uppercase">
                                {editing.id ? "Edit" : "New"} {editing.kind} brand
                            </div>
                            <button onClick={() => setEditing(null)}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4 grid gap-3">
                            <div>
                                <label className="label-kicker mb-1 block">Name *</label>
                                <input
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    className="input-field"
                                    placeholder="Garrett, BorgWarner, Melett..."
                                    data-testid="brand-name-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Kind *</label>
                                <select
                                    value={editing.kind}
                                    onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                                    className="input-field"
                                    data-testid="brand-kind-input"
                                >
                                    <option value="oem">OEM (original manufacturer)</option>
                                    <option value="aftermarket">Aftermarket</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Logo</label>
                                <ImageUploader
                                    value={editing.logo_url}
                                    onChange={(v) => setEditing({ ...editing, logo_url: v })}
                                    testId="brand-logo-uploader"
                                    recommended="320×160px · PNG transparent · ~30KB · 2:1 horizontal"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Website</label>
                                <input
                                    value={editing.website}
                                    onChange={(e) => setEditing({ ...editing, website: e.target.value })}
                                    className="input-field"
                                    placeholder="https://..."
                                    data-testid="brand-website-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Order</label>
                                <input
                                    type="number"
                                    value={editing.order}
                                    onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                                    className="input-field"
                                    placeholder="0"
                                    data-testid="brand-order-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Description</label>
                                <textarea
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    className="input-field min-h-[80px]"
                                    data-testid="brand-description-input"
                                />
                            </div>
                            {error && <div className="text-red-600 text-xs font-mono">{error}</div>}
                        </div>
                        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline" data-testid="cancel-brand-button">
                                Cancel
                            </button>
                            <button onClick={save} className="btn-primary" disabled={!editing.name} data-testid="save-brand-button">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
        </div>
    );
}
